import { motion } from 'framer-motion'
import { ArrowRight, Leaf, Shield, Droplets } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

const Hero = () => {
  const navigate = useNavigate()

  const handleProductsClick = () => {
    navigate('/products')
  }

  const handleContactClick = () => {
    navigate('/contact')
  }

  return (
    <section className="relative min-h-screen flex items-center pt-16 overflow-hidden">
      {/* Background Image avec overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url("images/14968325765b461e28740c2.jpg")',
        }}
      >
        {/* Overlay pour améliorer la lisibilité */}
        <div className="absolute inset-0 bg-black/40"></div>
        {/* Pattern subtil par-dessus l'image */}
        <div className="absolute inset-0 moroccan-pattern opacity-10"></div>
      </div>
      
      {/* Gradient supplémentaire pour le dégradé de couleur */}
      <div className="absolute inset-0 bg-gradient-to-br from-green-900/30 via-transparent to-green-800/20"></div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Partie texte */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center lg:text-left"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-white/90 backdrop-blur-sm text-green-700 px-4 py-2 rounded-full font-semibold mb-6"
            >
              <Leaf className="w-5 h-5" />
              100% Naturel & Écologique
            </motion.div>
            
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-4 sm:mb-6 leading-tight">
              Isolation
              <span className="block bg-gradient-to-r from-green-300 to-green-100 bg-clip-text text-transparent">
                Écologique
              </span>
              Nouvelle Génération
            </h1>
            
            <p className="text-lg sm:text-xl text-green-50 mb-6 sm:mb-8 leading-relaxed max-w-2xl mx-auto lg:mx-0">
              ECOTHERM révolutionne l'isolation avec des produits 100% naturels, sans eau, sans allergènes. Protection maximale pour votre santé et l'environnement.
            </p>
            
            {/* Boutons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-8 sm:mb-12 justify-center lg:justify-start">
              <button 
                onClick={handleProductsClick}
                className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                Découvrir Nos Produits
                <ArrowRight className="w-5 h-5" />
              </button>
              <button 
                onClick={handleContactClick}
                className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white border border-white/20 px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold transition-all duration-200 hover:shadow-lg"
              >
                Contactez-Nous
              </button>
            </div>
            
            {/* Features */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 max-w-md mx-auto lg:mx-0">
              {[
                { icon: Leaf, label: '100% Naturel' },
                { icon: Droplets, label: 'Sans Eau' },
                { icon: Shield, label: 'Anti-Radon' }
              ].map((item, index) => {
                const IconComponent = item.icon
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.8 + index * 0.1 }}
                    className="flex flex-col items-center text-center bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 hover:bg-white/15 transition-all duration-300"
                  >
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-500/20 rounded-lg flex items-center justify-center mb-2">
                      <IconComponent className="w-5 h-5 sm:w-6 sm:h-6 text-green-300" />
                    </div>
                    <span className="text-sm font-semibold text-white">{item.label}</span>
                  </motion.div>
                )
              })}
            </div>
          </motion.div>
          
          {/* Partie statistiques */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative mt-8 lg:mt-0"
          >
            <div className="relative w-full aspect-square max-w-md lg:max-w-lg mx-auto">
              {/* Animation de fond */}
              <motion.div
                animate={{ 
                  rotate: 360,
                }}
                transition={{ 
                  duration: 20,
                  repeat: Infinity,
                  ease: "linear"
                }}
                className="absolute inset-0 bg-gradient-to-br from-green-400 to-green-600 rounded-full opacity-30 blur-3xl"
              />
              
              {/* Carte des statistiques */}
              <div className="relative bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl p-6 sm:p-8 border border-white/20">
                <div className="grid grid-cols-2 gap-4 sm:gap-6">
                  {[
                    { value: '0.045', unit: 'W/mk', label: 'Conductivité Thermique' },
                    { value: 'A2', unit: '', label: 'Classement Feu' },
                    { value: '30%', unit: '', label: 'Économie d\'Énergie' },
                    { value: '0', unit: '', label: 'Substances Nocives' }
                  ].map((stat, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                      className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-4 sm:p-6 text-center border border-green-200 hover:shadow-lg transition-all duration-300"
                    >
                      <div className="text-2xl sm:text-3xl font-bold text-green-700 mb-1">
                        {stat.value}
                        {stat.unit && <span className="text-sm sm:text-lg ml-1">{stat.unit}</span>}
                      </div>
                      <div className="text-xs sm:text-sm text-gray-600 font-semibold leading-tight">
                        {stat.label}
                      </div>
                    </motion.div>
                  ))}
                </div>
                
                {/* Badge supplémentaire */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.9 }}
                  className="mt-6 text-center"
                >
                  <div className="inline-flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg">
                    <Shield className="w-4 h-4" />
                    Certifié Eco-Responsable
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      
      {/* Gradient de transition en bas */}
      <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white to-transparent" />
    </section>
  )
}

export default Hero